import {
  IconChartLine,
  IconDiscover,
  IconDraft,
  IconLab,
  IconLightBulb,
} from '@spotify-internal/encore-web';

import { type DefaultPrompt } from './components/ChatStarter';

export const TOOL_PANEL_ANIMATION_MS = 160;

export const MAX_IMAGES_PER_TURN = 4;
export const MAX_IMAGE_BYTES_PER_TURN = 10 * 1024 * 1024;

export type ChatSkill = {
  label: string;
  example: string;
  prompts: string[];
};

export const CHAT_SKILLS: ChatSkill[] = [
  {
    label: 'Explore opportunities',
    example: 'Find a promising problem to solve',
    prompts: [
      'Where could we improve the user experience?',
      'What patterns appear across recent feedback?',
      'Help me explore an opportunity in my project.',
      'Which user problem should we prioritize next?',
    ],
  },
  {
    label: 'Draft a PRD',
    example: 'Turn an idea into a clear product brief',
    prompts: [
      'Turn my idea into a PRD with clear goals.',
      'Help me define the problem and success criteria.',
      'Review my brief for gaps and assumptions.',
      'What requirements should this feature include?',
    ],
  },
  {
    label: 'Prototype an idea',
    example: 'Make a possible solution tangible',
    prompts: [
      'Build a quick prototype of my idea.',
      'Explore two approaches to this user flow.',
      'Turn my product brief into a prototype.',
      'How could we simplify this interaction?',
    ],
  },
  {
    label: 'Review recordings',
    example: 'See where people get stuck',
    prompts: [
      'Review recordings to find where users get stuck.',
      'Help me investigate a frustrating user journey.',
      'Summarize common patterns in these sessions.',
      'Where do users abandon this journey?',
    ],
  },
  {
    label: 'Design an experiment',
    example: 'Build a plan to test your hypothesis',
    prompts: [
      'Help me test a product idea with an experiment.',
      'Which success metrics should this test use?',
      'Review my experiment plan before launch.',
      'How long should this experiment run?',
    ],
  },
  {
    label: 'Analyze data',
    example: 'Explore trends and results',
    prompts: [
      'How has engagement changed over the last month?',
      'Which user segments explain the change?',
      'What do these experiment results suggest?',
      'Which metrics should we investigate next?',
    ],
  },
];

export const ONBOARDING_PROMPT: DefaultPrompt = {
  id: 'show-me-around',
  label: 'Show me around',
  message:
    'Show me around this project. Help me understand what it covers and what I can start with.',
  icon: IconDiscover,
};

export const DEFAULT_PROMPTS: DefaultPrompt[] = [
  ONBOARDING_PROMPT,
  {
    id: 'live-ab-tests',
    label: 'Live A/B tests',
    message:
      'Count how many A/B tests are currently live. List the most 5 most recently created by name and owner.',
    icon: IconLab,
  },
  {
    id: 'summarize-experiment',
    label: 'Summarize experiment',
    message: 'Summarize the experiment that was most recently created by me.',
    icon: IconDraft,
  },
  {
    id: 'explain-time-windows',
    label: 'Explain time windows',
    message:
      'Explain how time windows in metrics work. Draw an illustration to present your answer visually.',
    icon: IconLightBulb,
  },
  {
    id: 'visualize-metric',
    label: 'Visualize metric',
    message:
      "Find a metric used in a recently ended experiment. Query that underlying data source and create a graph showing the metric's value over the last 7 days.",
    icon: IconChartLine,
  },
];

export const HOME_PROMPTS: DefaultPrompt[] = [
  {
    id: 'confidence-overview',
    label: 'Show me what Confidence can do',
    message:
      'Show me what Confidence can do. Walk me through the key features and how I can use them.',
  },
  {
    id: 'explore-use-case',
    label: 'Explore my use case',
    message:
      'Help me explore my use case. I want to understand how Confidence can help with my specific needs.',
  },
  {
    id: 'plan-experiment',
    label: 'Help me plan an experiment',
    message:
      'Help me plan an experiment. Guide me through designing a test for my product idea.',
  },
  {
    id: 'next-steps',
    label: 'What should I do next',
    message:
      'What should I do next? Look at my recent activity and suggest a good next step.',
  },
];
